<script setup lang="ts">
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { BaseIcon } from '@/components/Base';
import { detectDeviceType } from '@/scripts/DeviceDetection';

const { t } = useI18n();

const deviceType = computed(() => detectDeviceType());

const tutorialSteps = computed(() => {
  switch (deviceType.value) {
    case 'ios-safari':
      return [
        { icon: 'settings', text: t('video.tutorial.iosSafari.step1') },
        { icon: 'safari', text: t('video.tutorial.iosSafari.step2') },
        { icon: 'camera', text: t('video.tutorial.iosSafari.step3') },
        { icon: 'refresh', text: t('video.tutorial.iosSafari.step4') },
      ];
    case 'ios-chrome':
      return [
        { icon: 'settings', text: t('video.tutorial.iosChrome.step1') },
        { icon: 'chrome', text: t('video.tutorial.iosChrome.step2') },
        { icon: 'camera', text: t('video.tutorial.iosChrome.step3') },
        { icon: 'refresh', text: t('video.tutorial.iosChrome.step4') },
      ];
    case 'android-chrome':
      return [
        { icon: 'more-vertical', text: t('video.tutorial.androidChrome.step1') },
        { icon: 'settings', text: t('video.tutorial.androidChrome.step2') },
        { icon: 'camera', text: t('video.tutorial.androidChrome.step3') },
        { icon: 'refresh', text: t('video.tutorial.androidChrome.step4') },
      ];
    case 'android-other':
      return [
        { icon: 'settings', text: t('video.tutorial.androidOther.step1') },
        { icon: 'camera', text: t('video.tutorial.androidOther.step2') },
        { icon: 'refresh', text: t('video.tutorial.androidOther.step3') },
      ];
    default:
      return [
        { icon: 'settings', text: t('video.tutorial.generic.step1') },
        { icon: 'camera', text: t('video.tutorial.generic.step2') },
        { icon: 'refresh', text: t('video.tutorial.generic.step3') },
      ];
  }
});

const browserName = computed(() => {
  switch (deviceType.value) {
    case 'ios-safari': return 'Safari';
    case 'ios-chrome': return 'Chrome';
    case 'android-chrome': return 'Chrome';
    case 'android-other': return t('video.tutorial.browser');
    default: return t('video.tutorial.browser');
  }
});
</script>

<template>
  <div class="tutorial">
    <div class="tutorial__header">
      <BaseIcon name="end-video" :size="48" />
      <h2>{{ t('video.tutorial.title') }}</h2>
      <p class="tutorial__subtitle">{{ t('video.tutorial.subtitle', { browser: browserName }) }}</p>
    </div>

    <ol class="tutorial__steps">
      <li v-for="(step, index) in tutorialSteps" :key="index" class="tutorial__step">
        <span class="tutorial__step-number">{{ index + 1 }}</span>
        <span class="tutorial__step-text">{{ step.text }}</span>
      </li>
    </ol>
  </div>
</template>

<style scoped>
.tutorial {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--spacing-lg);
  padding: var(--spacing-lg);
  text-align: center;
  color: var(--color-text-light);
}

.tutorial__header {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--spacing-sm);
}

.tutorial__header h2 {
  margin: 0;
  font-size: var(--font-size-lg);
  font-weight: 600;
}

.tutorial__subtitle {
  margin: 0;
  font-size: var(--font-size-sm);
  opacity: 0.8;
}

.tutorial__steps {
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: var(--spacing-md);
  text-align: left;
  width: 100%;
  max-width: 300px;
}

.tutorial__step {
  display: flex;
  align-items: flex-start;
  gap: var(--spacing-sm);
}

.tutorial__step-number {
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 28px;
  height: 28px;
  background-color: var(--color-primary);
  color: var(--color-text-light);
  border-radius: 50%;
  font-size: var(--font-size-sm);
  font-weight: 600;
  flex-shrink: 0;
}

.tutorial__step-text {
  font-size: var(--font-size-md);
  line-height: 1.4;
  padding-top: 2px;
}
</style>
